import React, { JSX, useState } from 'react';
import Icons from '../atoms/Icons';
import {
    Field,
    Image as JssImage,
    ImageField,
    Text,
    RichText,
    RichTextField,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { ItemValue } from 'components/types/ListItem';
import { StyleguideComponentProps } from 'lib/component-props';
import { classNames } from 'assets/utils/helper';
import { getMarginSize } from 'assets/utils/getMarginSize';

type ContentExpanderProps = {
    fields: {
        Image: ImageField;
        Title: Field<string>;
        Description: RichTextField;
    };
};

type ContentExpanderParentProps = StyleguideComponentProps & {
    fields: {
        Headline: Field<string>;
        Description: RichTextField;
        'Text Alignment': ItemValue;
        'Expander Content': Array<ContentExpanderProps>;
        'Background Color'?: ItemValue;
        MarginSize: {
            fields: {
                heading: {
                    value: string;
                };
            };
        };
    };
};

const ContentExpanderParent = (
    props: ContentExpanderParentProps
): JSX.Element => {
    const { fields } = props;
    const [openIndex, setOpenIndex] = useState<number | null>(null);

    const textAlignmentField = fields?.['Text Alignment'];
    const alignment =
        textAlignmentField?.name?.toLowerCase() === 'centered' ? 'center' : 'left';

    const expanderItems = fields?.['Expander Content'] || [];
    const marginSize = getMarginSize(
        props?.fields?.MarginSize?.fields?.heading?.value
    );

    const isFromLinkOrButton = (e: React.MouseEvent<HTMLElement>) => {
        const linkOrButton = (e.target as HTMLElement).closest('a, button');
        return !!linkOrButton && linkOrButton !== e.currentTarget;
    };

    const handleClick = (id: number, isOpen: boolean) => {
        const openItem = document.querySelector('.ce-row.is-open');

        let scrollAdjustment = 0;

        if (openItem) {
            const rect = openItem.getBoundingClientRect();

            if (rect.top < 0) {
                scrollAdjustment = Math.min(rect.height, -rect.top);
            }
        }

        setOpenIndex(isOpen ? null : id);

        requestAnimationFrame(() => {
            window.scrollBy({ top: -scrollAdjustment, left: 0, behavior: 'smooth' });
        });
    };

    return (
        <div
            className={classNames('overhauled ce-expander bg-primary', marginSize)}
            aria-label="Content Expander"
            id={`content-expander-parent-${props.rendering?.uid}`}
        >
            <div className="jss-inner-container outer-vertical-spacing">
                <div className={`ce-header ce-header--${alignment}`}>
                    <h2 className="ce-headline">
                        <Text field={fields.Headline} />
                    </h2>
                    {fields?.Description?.value && (
                        <div className="ce-intro">
                            <RichText field={fields.Description} />
                        </div>
                    )}
                </div>
                <div className="ce-items-container">
                    {expanderItems.map((child, id) => {
                        const isOpen = openIndex === id;
                        const panelId = `ce-panel-${id}`;

                        return (
                            <div
                                key={id}
                                className={`ce-row ${isOpen ? 'is-open' : ''}`}
                                role="button"
                                tabIndex={0}
                                aria-controls={panelId}
                                aria-expanded={isOpen}
                                onClick={e => {
                                    if (isFromLinkOrButton(e)) return;
                                    handleClick(id, isOpen);
                                }}
                                onKeyDown={e => {
                                    if (e.target !== e.currentTarget) return;
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleClick(id, isOpen);
                                    }
                                }}
                            >
                                {child.fields.Image?.value && (
                                    <div className="ce-media">
                                        <JssImage field={child.fields.Image} className="ce-img" />
                                    </div>
                                )}

                                <h3 className="ce-title osf-display-h3">
                                    <Text field={child.fields.Title} />
                                </h3>

                                <button
                                    type="button"
                                    className="ce-action"
                                    aria-controls={panelId}
                                    aria-expanded={isOpen}
                                    onClick={e => {
                                        e.stopPropagation();
                                        handleClick(id, isOpen);
                                    }}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            handleClick(id, isOpen);
                                        }
                                    }}
                                    aria-label="Content Expander"
                                >
                                    <Icons id="chevron-down" className="ce-chevron" />
                                </button>

                                <div id={panelId} className="ce-desc" aria-hidden={!isOpen}>
                                    <RichText field={child.fields.Description} />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default ContentExpanderParent;
